/**
 * The menu as the layout draws it (the Navigation review, slice (a)): the desktop sidebar, its icon rail on a tablet,
 * and the drawer on a phone. The areas and their items come from lib/nav/menu.ts.
 *   - lg+ (desktop): each area's heading, then its items with their labels.
 *   - md..lg (tablet): the icon rail, each item its own icon and its label in a tooltip, a divider between areas.
 *   - <md (phone): the drawer, with each area's heading.
 * A hub's item (slice (c)) opens the tab its person last used in that hub, or its first, and is active on any of its
 * tabs' pages.
 */
import { Link, useLocation, useMatch } from "react-router-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { BrandMark } from "@/lib/ui/brand-mark";
import { Button } from "@/lib/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/lib/ui/tooltip";
import { hubEntryOf, tabMatches } from "@/lib/nav/hubs";
import { lastHubTab } from "@/lib/nav/hub-memory";
import { hubItemActive, type MenuArea, type MenuItem } from "@/lib/nav/menu";

/**
 * The item that is open: the page's own text, semibold, on the muted surface, with a brand bar at its start. The
 * brand's colour on its own tint read at 1.39:1 in the dark theme (the graphite brand), below the 4.5:1 text needs.
 */
const ACTIVE = "bg-surface-muted font-semibold text-on-surface shadow-[inset_2px_0_0_rgb(var(--color-brand))]";
const IDLE = "text-on-surface-muted hover:bg-surface-muted hover:text-on-surface";

/**
 * Where an item links, and whether it is the open one. Most items match as NavLink would (a page under the item's
 * address is active too, but where `end` is set); a hub's item is active on any of its tabs' pages, where it links to
 * the tab open, and elsewhere opens the tab `person` last used there, or its first.
 */
function useItemLink(item: MenuItem, person: string): { to: string; active: boolean } {
  const { pathname } = useLocation();
  const exact = useMatch({ path: item.to, end: !!item.end }) !== null;
  if (!item.hub || !item.tabs) return { to: item.to, active: exact };
  const here = item.tabs.find((t) => tabMatches(t, pathname));
  const to = here?.to ?? hubEntryOf(item.tabs, lastHubTab(person, item.hub)) ?? item.to;
  return { to, active: hubItemActive(item, pathname) };
}

/** `person` is the signed-in person's id: whose remembered tab a hub's item opens. */
export function DesktopMenu({ areas, person = "" }: { areas: MenuArea[]; person?: string }) {
  return (
    <aside className="hidden md:flex md:w-16 lg:w-60 md:shrink-0 md:flex-col md:border-r md:border-border md:bg-surface-elevated">
      <nav aria-label="Main" className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-4 lg:px-3">
        {areas.map((area, i) => (
          <div key={area.id} data-menu-area={area.id} className={cn("flex flex-col gap-0.5", i > 0 && "mt-4")}>
            {/* The icon rail has no room for a heading: a divider marks where an area starts. */}
            {i > 0 && <div aria-hidden className="mx-2 mb-2 border-t border-border lg:hidden" />}
            <div className="hidden px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-on-surface-muted lg:block">
              {area.label}
            </div>
            {area.items.map((item) => (
              <DesktopItem key={item.to} item={item} person={person} />
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}

function DesktopItem({ item, person }: { item: MenuItem; person: string }) {
  const Icon = item.icon;
  // The tooltip's trigger (Radix Slot, asChild) joins class names as strings, so a className function (NavLink's) would
  // reach the DOM as its own source text, and the item would lose its layout and its active look: the item is a Link
  // with a string class, its active state and aria-current its own (useItemLink).
  const { to, active } = useItemLink(item, person);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={to}
          aria-current={active ? "page" : undefined}
          // The icon rail hides the label, so the link names itself (#1211 review, LOW 4).
          aria-label={item.mark ? `${item.label}, ${item.mark}` : item.label}
          className={cn(
            "flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors lg:px-3",
            active ? ACTIVE : IDLE,
          )}
        >
          {item.mark ? (
            <span className="relative flex shrink-0">
              <Icon className="size-4" />
              {/* The icon rail shows the mark on the icon; the full sidebar beside the label. */}
              <span aria-hidden className="absolute -right-1 -top-1 size-2 rounded-full bg-brand ring-2 ring-surface-elevated lg:hidden" />
            </span>
          ) : (
            <Icon className="size-4 shrink-0" />
          )}
          <span className="hidden lg:inline">{item.label}</span>
          {item.mark && (
            <>
              <span aria-hidden className="ml-auto hidden size-2 shrink-0 rounded-full bg-brand lg:inline-block" />
              <span className="sr-only">{`, ${item.mark}`}</span>
            </>
          )}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" className="lg:hidden">
        {item.label}
        {item.mark ? ` · ${item.mark}` : ""}
      </TooltipContent>
    </Tooltip>
  );
}

export function MobileMenu({
  open,
  onClose,
  areas,
  productName,
  orgName,
  person = "",
}: {
  open: boolean;
  onClose: () => void;
  areas: MenuArea[];
  productName: string;
  orgName: string;
  person?: string;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
      <button
        type="button"
        aria-label="Close navigation"
        className="absolute inset-0 bg-on-surface/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-64 flex-col border-r border-border bg-surface-elevated shadow-xl animate-fade-in">
        <div className="flex h-14 items-center justify-between border-b border-border px-4">
          <div className="flex items-center gap-2">
            <BrandMark className="size-7" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold text-on-surface">{productName}</span>
              <span className="text-[11px] text-on-surface-muted">{orgName}</span>
            </div>
          </div>
          <Button variant="ghost" size="icon" aria-label="Close navigation" onClick={onClose}>
            <X className="size-5" />
          </Button>
        </div>
        <nav aria-label="Main" className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
          {areas.map((area, i) => (
            <div key={area.id} data-menu-area={area.id} className={cn("flex flex-col gap-0.5", i > 0 && "mt-4")}>
              <div className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-on-surface-muted">
                {area.label}
              </div>
              {area.items.map((item) => (
                <MobileItem key={item.to} item={item} person={person} onClose={onClose} />
              ))}
            </div>
          ))}
        </nav>
      </aside>
    </div>
  );
}

function MobileItem({ item, person, onClose }: { item: MenuItem; person: string; onClose: () => void }) {
  const Icon = item.icon;
  const { to, active } = useItemLink(item, person);
  return (
    <Link
      to={to}
      onClick={onClose}
      aria-current={active ? "page" : undefined}
      className={cn("flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors", active ? ACTIVE : IDLE)}
    >
      <Icon className="size-4 shrink-0" />
      <span>{item.label}</span>
      {item.mark && (
        <>
          <span aria-hidden className="ml-auto size-2 shrink-0 rounded-full bg-brand" />
          <span className="sr-only">{`, ${item.mark}`}</span>
        </>
      )}
    </Link>
  );
}
